export type BookingMode = "staah" | "link_out" | "none";
export type BookingEngine = "swiftbook" | "custom";

export const BOOKING_MODE_OPTIONS: { value: BookingMode; label: string }[] = [
  { value: "staah", label: "In-chat booking (STAAH)" },
  { value: "link_out", label: "Link to our booking engine" },
];

export const BOOKING_ENGINE_OPTIONS: { value: BookingEngine; label: string }[] = [
  { value: "swiftbook", label: "SwiftBook" },
  { value: "custom", label: "Custom" },
];

export const DEFAULT_DATE_FORMAT = "yyyy-MM-dd";
export const DATE_FORMATS = ["yyyy-MM-dd", "dd/MM/yyyy", "MM/dd/yyyy", "dd-MM-yyyy", "yyyyMMdd"];

export const PLACEHOLDERS = ["propertyId", "checkin", "checkout", "nights", "adults", "children", "rooms", "promo"];

export const SWIFTBOOK_TEMPLATE =
  "https://www.swiftbook.io/inst/#/home?propertyId={propertyId}&JDRN=Y&checkIn={checkin}&checkOut={checkout}" +
  "&noofrooms={rooms}&adult0={adults}&child0={children}";

export interface BookingSettings {
  booking_mode?: string;
  booking_enabled?: boolean;
  booking_engine?: string;
  booking_engine_property_id?: string;
  booking_url_template?: string;
  booking_date_format?: string;
}

/** ``booking_mode`` of a saved record, derived from the legacy ``booking_enabled`` flag when absent. */
export function bookingModeOf(data: BookingSettings | null | undefined): BookingMode {
  const mode = data?.booking_mode;
  if (mode === "staah" || mode === "link_out" || mode === "none") return mode;
  return data?.booking_enabled === false ? "none" : "staah";
}

const pad = (n: number) => String(n).padStart(2, "0");

/** *date* (a local ``Date``) in one of ``DATE_FORMATS``. */
export function formatDate(date: Date, format: string): string {
  const yyyy = String(date.getFullYear());
  const MM = pad(date.getMonth() + 1);
  const dd = pad(date.getDate());
  switch (format) {
    case "dd/MM/yyyy":
      return `${dd}/${MM}/${yyyy}`;
    case "MM/dd/yyyy":
      return `${MM}/${dd}/${yyyy}`;
    case "dd-MM-yyyy":
      return `${dd}-${MM}-${yyyy}`;
    case "yyyyMMdd":
      return `${yyyy}${MM}${dd}`;
    default:
      return `${yyyy}-${MM}-${dd}`;
  }
}

/** ``yyyy-MM-dd`` (a date input's value) as a local ``Date``, or ``null``. */
export function parseIsoDate(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || "");
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isNaN(date.getTime()) ? null : date;
}

type Values = Record<string, string | undefined>;

function fill(text: string, values: Values): string | null {
  let missing = false;
  const filled = text.replace(/\{(\w+)\}/g, (placeholder, name) => {
    const value = values[name];
    if (value === undefined || value === "") {
      missing = true;
      return placeholder;
    }
    return encodeURIComponent(value);
  });
  return missing ? null : filled;
}

/**
 * *template* with its placeholders replaced by URL-encoded *values*. Query
 * parameters whose placeholder has no value are dropped (also inside a
 * ``#/route?query`` fragment); ``null`` when a placeholder outside a query
 * parameter has no value. Mirrors ``build_booking_url`` in the chatbot.
 */
export function fillTemplate(template: string, values: Values): string | null {
  const parts = template.split(/([?&])/);
  let url = fill(parts[0], values);
  if (url === null) return null;
  let queryOpen = false;
  for (let i = 1; i < parts.length; i += 2) {
    if (parts[i] === "?") queryOpen = false;
    const hashAt = parts[i + 1].indexOf("#");
    const param = hashAt === -1 ? parts[i + 1] : parts[i + 1].slice(0, hashAt);
    const filled = param ? fill(param, values) : null;
    if (filled) {
      url += (queryOpen ? "&" : "?") + filled;
      queryOpen = true;
    }
    if (hashAt !== -1) {
      const tail = fill(parts[i + 1].slice(hashAt + 1), values);
      if (tail === null) return null;
      url += "#" + tail;
      queryOpen = false;
    }
  }
  return url;
}

/** The template and date format the settings build links from. */
export function effectiveTemplate(settings: BookingSettings): { template: string; dateFormat: string } {
  if (settings.booking_engine === "swiftbook") {
    return { template: SWIFTBOOK_TEMPLATE, dateFormat: DEFAULT_DATE_FORMAT };
  }
  return {
    template: (settings.booking_url_template || "").trim(),
    dateFormat: settings.booking_date_format || DEFAULT_DATE_FORMAT,
  };
}

/** A filled link for trying the settings out: check-in today + *daysAhead*, one night, 2 adults. */
export function testBookingUrl(settings: BookingSettings, today: Date = new Date(), daysAhead = 30): string | null {
  const { template, dateFormat } = effectiveTemplate(settings);
  if (!template) return null;
  const checkin = new Date(today.getFullYear(), today.getMonth(), today.getDate() + daysAhead);
  const checkout = new Date(checkin.getFullYear(), checkin.getMonth(), checkin.getDate() + 1);
  return fillTemplate(template, {
    propertyId: (settings.booking_engine_property_id || "").trim(),
    checkin: formatDate(checkin, dateFormat),
    checkout: formatDate(checkout, dateFormat),
    nights: "1",
    adults: "2",
    children: "0",
    rooms: "1",
  });
}

export function isHttpUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

/** Validation errors for the link-out settings, keyed by field name (empty when valid). */
export function bookingSettingsErrors(settings: BookingSettings): Record<string, string> {
  const errors: Record<string, string> = {};
  if (settings.booking_mode !== "link_out") return errors;
  if (settings.booking_engine === "swiftbook") {
    if (!(settings.booking_engine_property_id || "").trim()) {
      errors.booking_engine_property_id = "SwiftBook property ID is required!";
    }
    return errors;
  }
  const template = (settings.booking_url_template || "").trim();
  if (!template) {
    errors.booking_url_template = "Booking URL template is required!";
  } else if (!template.includes("{checkin}") || !template.includes("{checkout}")) {
    errors.booking_url_template = "The template must contain {checkin} and {checkout}.";
  } else if (!isHttpUrl(template.replace(/\{\w+\}/g, "x"))) {
    errors.booking_url_template = "Enter a valid http(s) URL.";
  }
  return errors;
}

/** Whether the Cancel Booking button needs its own URL: only a custom booking engine has no known cancel page. */
export function needsCancelUrl(settings: BookingSettings): boolean {
  return bookingModeOf(settings) === "link_out" && (settings.booking_engine || "swiftbook") === "custom";
}

/** Validation errors for the optional Cancel Booking URL (empty when blank, valid or when the button is hidden). */
export function cancelBookingErrors(
  settings: BookingSettings,
  cancel: { label?: string; enabled?: boolean; content?: { url?: string } },
): Record<string, string> {
  const shown = cancel.enabled === undefined ? Boolean((cancel.label || "").trim()) : cancel.enabled;
  if (!needsCancelUrl(settings) || !shown) return {};
  const url = (cancel.content?.url || "").trim();
  if (url && !isHttpUrl(url)) return { cancelUrl: "Enter a valid http(s) URL." };
  return {};
}

export interface DetectedTemplate {
  template: string;
  dateFormat: string;
  adultsDetected: boolean;
  childrenDetected: boolean;
}

/**
 * *template* with the query parameter whose value is *count* replaced by
 * ``{placeholder}``: one named like *name* wins, else (when *anyName*) the
 * first. ``null`` when no parameter matches.
 */
function replaceCount(template: string, count: string, name: RegExp, placeholder: string, anyName: boolean): string | null {
  if (!/^\d+$/.test(count)) return null;
  const params = [...template.matchAll(/([?&])([^=&#?]+)=([^&#]*)/g)].filter((m) => m[3] === count);
  const param = params.find((m) => name.test(m[2])) || (anyName ? params[0] : undefined);
  if (!param || param.index === undefined) return null;
  const replacement = `${param[1]}${param[2]}={${placeholder}}`;
  return template.slice(0, param.index) + replacement + template.slice(param.index + param[0].length);
}

/**
 * Turn a URL copied from a real search on the booking engine into a template:
 * the searched check-in/check-out (``yyyy-MM-dd``) are found in any supported
 * date format, raw or URL-encoded, and replaced by ``{checkin}``/``{checkout}``;
 * a query parameter equal to *adults* (preferring one named like "adult")
 * becomes ``{adults}``, and one named like "child"/"kid" equal to *children*
 * becomes ``{children}``. ``null`` when the dates are not in the URL.
 */
export function detectTemplate(
  sampleUrl: string,
  checkin: string,
  checkout: string,
  adults?: string,
  children?: string,
): DetectedTemplate | null {
  const url = (sampleUrl || "").trim();
  const inDate = parseIsoDate(checkin);
  const outDate = parseIsoDate(checkout);
  if (!url || !inDate || !outDate || outDate <= inDate) return null;

  let match: { format: string; ci: string; co: string } | undefined;
  for (const format of DATE_FORMATS) {
    const variants = [
      [formatDate(inDate, format), formatDate(outDate, format)],
      [encodeURIComponent(formatDate(inDate, format)), encodeURIComponent(formatDate(outDate, format))],
    ];
    const found = variants.find(([ci, co]) => url.includes(ci) && url.includes(co));
    if (found) {
      match = { format, ci: found[0], co: found[1] };
      break;
    }
  }
  if (!match) return null;

  const { format, ci, co } = match;
  let template = url.split(ci).join("{checkin}").split(co).join("{checkout}");

  const withChildren = replaceCount(template, (children || "").trim(), /child|kid/i, "children", false);
  if (withChildren) template = withChildren;
  const withAdults = replaceCount(template, (adults || "").trim(), /adult/i, "adults", true);
  if (withAdults) template = withAdults;

  return {
    template,
    dateFormat: format,
    adultsDetected: Boolean(withAdults),
    childrenDetected: Boolean(withChildren),
  };
}
